import { useEffect, useState } from "react";
import { X, Eye, EyeOff } from "lucide-react";
import { useStore, api } from "@/context/StoreContext";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function AuthModal() {
  const { authOpen, setAuthOpen, authMode, setAuthMode, setUser, user, toast, t } = useStore();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "", code: "" });
  const [view, setView] = useState("form"); // form | verify | verify-done | forgot | reset | reset-done
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (authOpen && authMode === "verify" && user && !user.email_verified) setView("verify");
    if (!authOpen) {
      setView("form");
      setError("");
      setNotice("");
      setForm({ name: "", email: "", phone: "", password: "", confirm: "", code: "" });
      setShowPw(false);
    }
  }, [authOpen, authMode]);

  if (!authOpen) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const backToForm = () => { setView("form"); setError(""); setNotice(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (authMode === "login") {
        const { data } = await api.post("/auth/login", { email: form.email, password: form.password });
        setUser(data);
        toast(`${t("auth.welcome")}, ${data.name}!`);
        setAuthOpen(false);
        setForm({ name: "", email: "", phone: "", password: "", confirm: "", code: "" });
        setShowPw(false);
      } else {
        if (form.password !== form.confirm) {
          setError(t("auth.pwMismatch"));
          setBusy(false);
          return;
        }
        const { data } = await api.post("/auth/register", {
          name: form.name, email: form.email, phone: form.phone, password: form.password,
        });
        setUser(data);
        toast(t("auth.created"));
        setView("verify");
        setForm((f) => ({ ...f, password: "", confirm: "", code: "" }));
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      if (typeof d === "string" && d.includes("currently unavailable")) setError(t("auth.unavailable"));
      else setError(formatApiErrorDetail(d));
    }
    setBusy(false);
  };

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/verify-email", { code: form.code.trim() });
      setUser((u) => ({ ...(u || {}), email_verified: true }));
      setView("verify-done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const resend = async () => {
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast(t("auth.resendOk"));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const forgot = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      setNotice(data.message || t("auth.forgotOk"));
      setView("reset");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const reset = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirm) {
      setError(t("auth.pwMismatch"));
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email: form.email, code: form.code.trim(), new_password: form.password });
      setView("reset-done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    }
    setBusy(false);
  };

  const emailIn = (testid, key = "auth.email", type = "email") => (
    <div className="field">
      <label>{t(key)}</label>
      <input data-testid={testid} type={type} value={form.email} onChange={set("email")} required />
    </div>
  );

  const pwIn = (testid, key = "auth.password", val = "password", min = 6) => (
    <div className="field">
      <label>{t(key)}</label>
      <div className="pw-field">
        <input
          data-testid={testid}
          type={showPw ? "text" : "password"}
          value={form[val]}
          onChange={set(val)}
          required
          minLength={min}
        />
        <button
          type="button"
          className="pw-eye"
          data-testid={`toggle-password-visibility-${val}`}
          aria-label={showPw ? "Hide password" : "Show password"}
          onClick={() => setShowPw((s) => !s)}
        >
          {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="modal show" data-testid="auth-modal">
      <div className="modal-card auth-card" data-lenis-prevent>
        <div className="modal-head">
          <div>
            <p className="section-title-small">PAMAK ACCOUNT</p>
            <h2>{view === "form" ? (authMode === "login" ? t("auth.login") : t("auth.register")) : view === "verify" ? t("auth.verifyTitle") : view === "forgot" ? t("auth.forgotTitle") : view === "reset" ? t("auth.resetTitle") : "PAMAK"}</h2>
          </div>
          <button className="close-btn" data-testid="auth-close-button" aria-label="Close" onClick={() => setAuthOpen(false)}>
            <X size={18} />
          </button>
        </div>

        {view === "form" && (
          <>
            <div className="auth-tabs">
              <button
                className={`auth-tab ${authMode === "login" ? "on" : ""}`}
                data-testid="auth-tab-login"
                onClick={() => setAuthMode("login")}
              >
                {t("auth.login")}
              </button>
              <button
                className={`auth-tab ${authMode === "register" ? "on" : ""}`}
                data-testid="auth-tab-register"
                onClick={() => setAuthMode("register")}
              >
                {t("auth.register")}
              </button>
            </div>

            <form onSubmit={submit} className="auth-form">
              {authMode === "register" && (
                <>
                  <div className="field">
                    <label>{t("auth.name")}</label>
                    <input data-testid="auth-name-input" value={form.name} onChange={set("name")} required />
                  </div>
                  <div className="field">
                    <label>{t("auth.phone")}</label>
                    <input data-testid="auth-phone-input" value={form.phone} onChange={set("phone")} />
                  </div>
                </>
              )}
              {emailIn("auth-email-input")}
              {pwIn("auth-password-input")}
              {authMode === "register" && pwIn("auth-confirm-input", "auth.confirmPw", "confirm")}

              {authMode === "login" && (
                <button
                  type="button"
                  className="link-btn"
                  data-testid="auth-forgot-link"
                  onClick={() => { backToForm(); setView("forgot"); }}
                >
                  {t("auth.forgotLink")}
                </button>
              )}

              <p className="error-text" data-testid="auth-error">{error}</p>

              <button className="checkout-btn" type="submit" data-testid="auth-submit-button" disabled={busy}>
                {authMode === "login" ? t("auth.login") : t("auth.register")}
              </button>
            </form>
          </>
        )}

        {view === "verify" && (
          <form onSubmit={verify} className="auth-form" data-testid="auth-verify-view">
            <p className="muted" data-testid="auth-verify-desc">{t("auth.verifyDesc", { email: user?.email || form.email })}</p>
            <div className="field">
              <label>{t("auth.codeLabel")}</label>
              <input
                data-testid="auth-verify-code-input"
                className="code-input"
                value={form.code}
                onChange={set("code")}
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                required
              />
            </div>
            <p className="error-text" data-testid="auth-error-verify">{error}</p>
            <button className="checkout-btn" type="submit" data-testid="auth-verify-button" disabled={busy}>
              {t("auth.verifyBtn")}
            </button>
            <button className="link-btn" type="button" data-testid="auth-resend-button" onClick={resend} disabled={busy}>
              {t("auth.resendBtn")}
            </button>
          </form>
        )}

        {view === "verify-done" && (
          <div className="auth-form" data-testid="auth-verify-done">
            <p className="success-note" data-testid="auth-verify-ok">{t("auth.verifiedOk")}</p>
            <button className="checkout-btn" data-testid="auth-verify-done-btn" onClick={() => setAuthOpen(false)}>
              {t("auth.done")}
            </button>
          </div>
        )}

        {view === "forgot" && (
          <form onSubmit={forgot} className="auth-form" data-testid="auth-forgot-view">
            <p className="muted">{t("auth.forgotDesc")}</p>
            {emailIn("auth-forgot-email-input")}
            <p className="error-text" data-testid="auth-error-forgot">{error}</p>
            <button className="checkout-btn" type="submit" data-testid="auth-forgot-button" disabled={busy}>
              {t("auth.forgotBtn")}
            </button>
            <button className="link-btn" type="button" data-testid="auth-forgot-back" onClick={backToForm}>
              {t("auth.backToLogin")}
            </button>
          </form>
        )}

        {view === "reset" && (
          <form onSubmit={reset} className="auth-form" data-testid="auth-reset-view">
            <p className="muted" data-testid="auth-forgot-ok">{notice || t("auth.forgotOk")}</p>
            <div className="field">
              <label>{t("auth.codeLabel")}</label>
              <input
                data-testid="auth-reset-code-input"
                className="code-input"
                value={form.code}
                onChange={set("code")}
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                required
              />
            </div>
            {pwIn("auth-reset-password-input", "auth.newPw", "password")}
            {pwIn("auth-reset-confirm-input", "auth.confirmPw", "confirm")}
            <p className="error-text" data-testid="auth-error-reset">{error}</p>
            <button className="checkout-btn" type="submit" data-testid="auth-reset-button" disabled={busy}>
              {t("auth.resetBtn")}
            </button>
          </form>
        )}

        {view === "reset-done" && (
          <div className="auth-form" data-testid="auth-reset-done">
            <p className="success-note" data-testid="auth-reset-ok">{t("auth.resetOk")}</p>
            <button
              className="checkout-btn"
              data-testid="auth-reset-login-button"
              onClick={() => { setAuthMode("login"); backToForm(); }}
            >
              {t("auth.backToLogin")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
